import { Button, Group } from "@mantine/core";
import { useState } from "react";
import { useConfiguratorComponents } from "../../hooks/useConfiguratorComponents";
import { ObjectName } from "../../utils/enums";
import { ConnectionWizard } from "../ConnectionWizard/ConnectionWizard";
import { ConsumerWizard } from "../ConsumerWizard/ConsumerWizard";
import { GasConnectionWizard } from "../GasConnectionWizard/GasConnectionWizard";
import { GeneratorWizard } from "../GeneratorWizard/GeneratorWizard";
import { GhostNodeWizard } from "../GhostNodeWizard/GhostNodeWizard";
import { MarketLocationWizard } from "../MarketLocationWizard/MarketLocationWizard";
import { MeteringLocationWizard } from "../MeteringLocationWizard/MeteringLocationWizard";
import { MeterWizard } from "../MeterWizard/MeterWizard";
import { BatteryStorageModal } from "../ObjectModals/BatteryStorageModal/BatteryStorageModal";
import { ComponentTable } from "./ComponentList/ComponentTable";

const BUTTON_IDS = {
  [ObjectName.Consumer]: "create-consumer-button",
  [ObjectName.Storage]: "create-storage-button",
  [ObjectName.Person]: "create-person-button",
  [ObjectName.Connection]: "create-connection-button",
  [ObjectName.GasConnection]: "create-gasconnection-button",
  [ObjectName.Meter]: "create-meter-button",
  [ObjectName.GhostNode]: "create-ghost-node-button"
};

export interface ComponentListContainerProps {
  objectName: ObjectName;
  objectDisplayName: string;
  siteId: number;
  variantId: number;
  projectId: string;
  graphCoordinates: { x: number; y: number };
  userHasAccess: boolean;
  setShowNotAccessibleModal: (show: boolean) => void;
}

function ComponentListContainer({
  objectName,
  objectDisplayName,
  siteId,
  variantId,
  projectId,
  graphCoordinates,
  userHasAccess,
  setShowNotAccessibleModal
}: ComponentListContainerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const siteOrVariantId = objectName === ObjectName.Person ? variantId : siteId;
  const { data: componentData } = useConfiguratorComponents(
    objectName,
    siteOrVariantId
  );

  const noComponentData = !!componentData && componentData.length === 0;
  const showButton = objectName !== ObjectName.GasConnection || noComponentData;

  function handleToggleModal() {
    setIsModalOpen(!isModalOpen);
  }

  return (
    <div className="ComponentListContainer">
      <ComponentTable
        objectName={objectName}
        projectId={projectId}
        setShowNotAccessibleModal={setShowNotAccessibleModal}
        siteId={siteId}
        userHasAccess={userHasAccess}
        variantId={variantId}
      />
      {showButton && (
        <Group justify="center" mt="xl">
          <Button
            color="primary"
            // currently `disabled` can't be used since on clicking the disabled button the modal opens
            // we should discuss this with the design team - maybe use a tooltip instead?
            data-disabled={!userHasAccess}
            id={BUTTON_IDS[objectName]}
            onClick={() => {
              if (!userHasAccess) {
                setShowNotAccessibleModal(true);
              } else {
                handleToggleModal();
              }
            }}
          >
            {objectDisplayName} erstellen
          </Button>
        </Group>
      )}
      {objectName === ObjectName.GhostNode && (
        <GhostNodeWizard
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.Connection && (
        <ConnectionWizard
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.GasConnection && (
        <GasConnectionWizard
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.Meter && (
        <MeterWizard
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          mode="create"
          projectId={projectId}
          siteId={siteId}
          variantId={variantId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.MarketLocation && (
        <MarketLocationWizard
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.MeteringLocation && (
        <MeteringLocationWizard
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.Consumer && (
        <ConsumerWizard
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.Storage && (
        <BatteryStorageModal
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleToggleModal}
        />
      )}
      {objectName === ObjectName.Generator && (
        <GeneratorWizard
          graphCoordinates={graphCoordinates}
          isOpen={isModalOpen}
          siteId={siteId}
          variantId={variantId}
          onClose={handleToggleModal}
        />
      )}
    </div>
  );
}

export { ComponentListContainer };
